import { defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, createVNode as _createVNode, vModelCheckbox as _vModelCheckbox, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from '../assets/twitter.png'


const _withScopeId = n => (_pushScopeId("data-v-cb0e3a3c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "pure-form pure-g" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("option", { value: undefined }, "Examples", -1))
const _hoisted_3 = ["value"]
const _hoisted_4 = {
  class: "pure-u-1-24",
  style: {"text-align":"center"}
}
const _hoisted_5 = { style: {"resize":"vertical","overflow":"auto","height":"20em"} }
const _hoisted_6 = {
  onsubmit: "return false",
  class: "pure-form pure-form-aligned"
}
const _hoisted_7 = ["value"]
const _hoisted_8 = { style: {"margin-right":"1rem"} }
const _hoisted_9 = { style: {"display":"inline-block"} }
const _hoisted_10 = { style: {"display":"inline-block","margin-left":"1rem","margin-right":"1rem"} }
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("summary", null, "More options", -1))
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { for: "compression_alg" }, "Compression:", -1))
const _hoisted_13 = ["value"]
const _hoisted_14 = { class: "pure-form pure-g" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { class: "pure-g" }
const _hoisted_17 = { key: 1 }
const _hoisted_18 = { class: "pure-g" }
const _hoisted_19 = { class: "pure-u-1" }

import {computed, nextTick, onMounted, ref, watch, defineAsyncComponent} from 'vue';
import {computedAsync, useDebounce} from '@vueuse/core';
import * as uaDeviceDetector from 'ua-device-detector';
import {type Transpiler} from "@/transpilers/Transpiler";
import {RubyTranspiler} from "@/transpilers/RubyTranspiler";
import {Es2017Transpiler, FuncEs2017Transpiler} from "@/transpilers/Es2017Transpiler";
import {type CompressionAlg} from "@/compression-algs/CompressionAlg";
import {DeflateAlg} from "@/compression-algs/DeflateAlg";
import {LZMAAlg} from "@/compression-algs/LZMAAlg";
import {Example, examples} from "@/examples";

// Encode code
type IStandaloneEditorConstructionOptions = Parameters<(typeof import("monaco-editor"))["editor"]["create"]>[1];


export default /*#__PURE__*/_defineComponent({
  __name: 'Nipp',
  setup(__props) {

const NippMonacoEditor = defineAsyncComponent(() => import('@/components/NippMonacoEditor.vue'));
async function encodeCode(code: string, compressor: (raw: string) => Promise<string>): Promise<string> {
  try {
    const binStr = await compressor(code);
    return btoa(binStr);
  } catch (err) {
    return "";
  }
}
// Decode code
async function decodeCode(encodedCode: string, decompressor: (compressed: string) => Promise<string>): Promise<string> {
  try {
    // Base64 => binary String
    const binStr = atob(encodedCode);
    return await decompressor(binStr);
  } catch (err) {
    return "";
  }
}

// Parse location.hash and return page title and code
function parseLocationHash(): { pageTitle: string, urlOptions: string[], encodedCode: string } {
  // Split by "/"
  const splited = location.hash.split("/");
  if(splited.length >= 3) {
    // Get title
    const title = decodeURIComponent(splited[0].substring(1).replace(/_/g, " "));
    // Get URL options
    const urlOptions = splited[1].split(",");
    // Get encoded code
    const encodedCode = splited.slice(2, splited.length).join("/");
    return {
      pageTitle: title,
      urlOptions: urlOptions,
      encodedCode: encodedCode
    };
  } else {
    return {
      pageTitle: "",
      urlOptions: [],
      encodedCode: ""
    }
  }
}

const visitWithoutFragment = window.location.hash === "";

if (window.name === "") {
  // window.name is used for identifier to restore input
  window.name = `nipp_${Math.random()}`;
}

const INPUT_SESSION_STORAGE_KEY = `${window.name}_input`;
const CONSOLE_CLEAR_STORAGE_KEY = 'clear_console_before_run';

const compressionAlgs = ref<readonly CompressionAlg[]>([
  DeflateAlg,
  LZMAAlg
]);
// Compression algorithm
const compressionAlg = ref<CompressionAlg>(compressionAlgs.value[0]);
// Page title
const pageTitle = ref("");
// Set empty string as default input
const inputText  = ref(window.sessionStorage.getItem(INPUT_SESSION_STORAGE_KEY) ?? "");
const debouncedInputText = useDebounce(inputText, 1000);
watch(debouncedInputText, () => {
  window.sessionStorage.setItem(INPUT_SESSION_STORAGE_KEY, debouncedInputText.value);
});
// Set empty string as default output
const outputText = ref("");
// Script
const script = ref("");
// Generated JavaScript code
const transpiledJsCode = ref("");
// Whether transpiled JS code is shown or not
const showTranspiledJsCode = ref(false);
// Executable function which return result
const executableFunction = ref<Function>(() => {return "";});
// Enable click-run or not
// (click-run: Non-realtime/non-reactive evaluation)
const enableClickRun = ref(false);
// Use promise-wait or not
const enablePromiseWait = ref(false);
// Use top-level await or not
const enableTopLevelAwait = ref(false);
const transpilers = ref<readonly Transpiler[]>([
  RubyTranspiler,
  Es2017Transpiler,
  FuncEs2017Transpiler
]);
// Set transpiler
const transpiler = ref(visitWithoutFragment ? Es2017Transpiler : RubyTranspiler);
// Error string
const errorStr = ref("");
// Whether error string is shown or not
const showError = ref(true);
// Whether has error or not
const hasError = ref(false);
// Text of click-run button
const clickRunButtonText = ref("");
// Use textarea instead of ace
const useTextarea = ref(false);
const scriptModified = ref(false);
const transpileElapsedMillis = ref(0);
const executeElapsedMillis = ref(0);
const blockingExecutionDetected = ref(false);
const consoleClearBeforeRun = ref(window.localStorage.getItem(CONSOLE_CLEAR_STORAGE_KEY) === "true");
watch(consoleClearBeforeRun, () => {
  window.localStorage.setItem(CONSOLE_CLEAR_STORAGE_KEY, consoleClearBeforeRun.value.toString());
});
const selectedExample = ref<Example>();
const loadExampleFirstTime = ref(true);
watch(selectedExample, async () => {
  if (selectedExample.value === undefined) {
    return;
  }
  if (loadExampleFirstTime.value && !window.confirm("Are you sure to load an example?")) {
    selectedExample.value = undefined;
    return;
  }
  loadExampleFirstTime.value = false;
  if ("urlFragment" in selectedExample.value) {
    location.hash = await selectedExample.value.urlFragment();
    location.reload();
  } else {
    script.value = await selectedExample.value.script();
    transpiler.value = selectedExample.value.transpier;
    enableClickRun.value = selectedExample.value.options.clickRun;
    enablePromiseWait.value = selectedExample.value.options.promiseWait;
    enableTopLevelAwait.value = selectedExample.value.options.topLevelAwait;
  }
});

watch(script, () => {
  scriptModified.value = true;
});

onMounted(async () => {
  // Get page title and code
  const titleAndCode = parseLocationHash();
  if (titleAndCode.urlOptions.includes("lzma")) {
    compressionAlg.value = LZMAAlg;
  }
  // Set page title
  pageTitle.value = titleAndCode.pageTitle;
  // Set <title>
  if (titleAndCode.pageTitle !== "") {
    document.title   = titleAndCode.pageTitle;
  }
  // Set decoded location.hash as default script
  script.value = await decodeCode(titleAndCode.encodedCode, compressionAlg.value.decompress);
  // Set enable-click-run
  enableClickRun.value = titleAndCode.urlOptions.includes("click_run");
  // Set enable-promise-wait
  enablePromiseWait.value = visitWithoutFragment ? true : titleAndCode.urlOptions.includes("promise_wait");
  // Set enable-top-level await
  enableTopLevelAwait.value = visitWithoutFragment ? true : titleAndCode.urlOptions.includes("top_level_await");
  // "async" option enables both
  if (titleAndCode.urlOptions.includes("async")) {
    enablePromiseWait.value = true;
    enableTopLevelAwait.value = true;
  }
  if (titleAndCode.urlOptions.includes("es2017")) {
    transpiler.value = Es2017Transpiler;
  } else if (titleAndCode.urlOptions.includes("func_es2017")) {
    transpiler.value = FuncEs2017Transpiler;
  }
  // Initialize library
  transpiler.value.initLibrary();
  // Set default value to global variable "INPUT"
  // TODO: duplicate code
  (window as any).INPUT = inputText.value;
  // Get device info
  const deviceInfo = uaDeviceDetector.parseUserAgent(window.navigator.userAgent);
  // Set click-run button text
  clickRunButtonText.value = "Run" + (deviceInfo.isDesktop() ? (deviceInfo.os === "mac"? "(⌘+Enter)" : "(Ctrl+Enter)") : "");
  // If enable click_run is disable
  if (!enableClickRun.value) {
    // Set default output
    setOutputText();
  }

  window.addEventListener('keydown', (e: WindowEventMap['keydown']) => {
    if((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      nextTick(()=>{
        // Run onclick click-run
        onClickClickRun();
      })
    }
  });
});

watch(pageTitle, async () => {
  // Set page title
  document.title = pageTitle.value;
});

const debouncedScript = useDebounce(script, computed(() => transpileElapsedMillis.value));
watch(debouncedScript, async () => {
  // Transpile
  await transpile();
});

const monacoOptions = computed<IStandaloneEditorConstructionOptions>(() => {
  const language = transpiler.value.id === RubyTranspiler.id ? 'ruby': 'javascript';
  return {
    language: language,
    minimap: { enabled: false },
    fontSize: 15,
    tabSize: 2,
    automaticLayout: true
  };
});

const urlTitlePart = computed<string>(() => {
  return pageTitle.value.replace(/%/g, "%25").replace(/_/g, "%5F").replace(/ /g, "_").replace(/\//g, "%2F");
});

// Generate options part
const urlOptionsPart = computed<string>(() => {
  const options: string[] = [];
  // (NOTE: transpiler:ruby is default so it should be pushed)
  if (transpiler.value.id === Es2017Transpiler.id) {
    options.push("es2017");
  } else if (transpiler.value.id === FuncEs2017Transpiler.id) {
    options.push("func_es2017");
  }
  // (NOTE: compression:deflate is default so it should be pushed)
  if (compressionAlg.value.id === LZMAAlg.id) {
    options.push("lzma");
  }
  // If click_run is enable
  if (enableClickRun.value) {
    options.push("click_run");
  }
  if (enablePromiseWait.value && enableTopLevelAwait.value) {
    options.push("async");
  } else {
    // If promise_wait is enable
    if (enablePromiseWait.value) {
      options.push("promise_wait");
    }
    // If top-level await is enable
    if (enableTopLevelAwait.value) {
      options.push("top_level_await");
    }
  }
  // Generate options part
  return options.join(",");
});

const urlEncodedCodePart = computedAsync<string | undefined>(async () => {
  return await encodeCode(debouncedScript.value, compressionAlg.value.compress);
});

const locationHash = computed<string | undefined>(() => {
  if (urlEncodedCodePart.value === undefined) {
    return undefined;
  }
  return urlTitlePart.value + "/" + urlOptionsPart.value + "/" + urlEncodedCodePart.value;
});

watch(locationHash, () => {
  if (locationHash.value === undefined) {
    return;
  }
  // Not change location.hash when user visited and not input script yet
  if (!scriptModified.value) {
    return;
  }
  // Change location hash to the code
  location.hash = locationHash.value;
});

// (NOTE: this is not typo. onclick "click_run")
function onClickClickRun() {
  // Set output text
  setOutputText();
}

async function onChangeTranspiler() {
  // Initialize library
  transpiler.value.initLibrary();
  // Ensure to call once
  transpiler.value.initLibrary = () => Promise.resolve();
  // Transpile
  await transpile();
}

async function transpile() {
  try {
    const startTime = new Date().getTime();
    // Transpile script and Set executable function
    const executableFunctionAndTraspiledJsCode = await transpiler.value.getExecutableFunctionAndTranspiledJsCode(script.value, enableTopLevelAwait.value);
    transpileElapsedMillis.value = new Date().getTime() - startTime;
    executableFunction.value = executableFunctionAndTraspiledJsCode.executableFunction;
    transpiledJsCode.value = executableFunctionAndTraspiledJsCode.transpiledJsCode;
    errorStr.value = "";
    hasError.value = false;
    // If enable click_run is disable
    if (!enableClickRun.value) {
      // Set output text
      setOutputText();
    }
  } catch (err: any) {
    // console.log("Transpile compile", err);
    errorStr.value = err.toString();
    hasError.value = true;
  }
}

watch(inputText, () => {
  // If enable click_run is disable
  if (!enableClickRun.value) {
    // Set output text
    setOutputText();
  }
});

// Set output text
function setOutputText() {
  // Set global INPUT string variable
  (window as any).INPUT = inputText.value;
  if (consoleClearBeforeRun.value) {
    console.clear();
  }
  try {
    blockingExecutionDetected.value = false;
    let blockingExecution = true;
    const timer = setTimeout(() => {
      blockingExecution = false;
    }, 10);
    const startTime = new Date().getTime();
    // Get output
    const output = executableFunction.value();
    // If promise-wait is enable
    if(enablePromiseWait.value) {
      outputText.value = "<The promise is not complete yet>";
      Promise.resolve(output)
        .then((res: any) => {
          executeElapsedMillis.value = new Date().getTime() - startTime;
          clearInterval(timer);
          nextTick(() => {
            outputText.value = res + "";
            // Set no error
            errorStr.value = "";
            hasError.value = false;
            if (blockingExecution && executeElapsedMillis.value > 50) {
              blockingExecutionDetected.value = true;
            }
          });
        })
        .catch((err: Error) => {
          nextTick(()=>{
            outputText.value = "<Promise error: " + err.toString() + ">";
            errorStr.value = err.toString();
            hasError.value = true;
          });
        });
    } else {
      executeElapsedMillis.value = new Date().getTime() - startTime;
      clearInterval(timer);
      // Set output text
      outputText.value = output + "";
      // Set no error
      errorStr.value = "";
      hasError.value = false;
      if (blockingExecution && executeElapsedMillis.value > 50) {
        blockingExecutionDetected.value = true;
      }
    }
  } catch (err: any) {
    // console.log("JS Runtime error", err);
    outputText.value = "";
    errorStr.value = err.toString();
    hasError.value = true;
  }
}

function shareOnTwitter() {
  // (from: http://d.hatena.ne.jp/osyo-manga/20140717/1405626111)
  const url = 'https://twitter.com/share?text='+encodeURIComponent(pageTitle.value)+"&url=" + encodeURIComponent(location.href)+"&hashtags=nipp";
  window.open(url,'','scrollbars=yes,width=500,height=300,');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("form", _hoisted_1, [
      _withDirectives(_createElementVNode("input", {
        type: "text",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pageTitle).value = $event)),
        placeholder: "App name",
        class: "pure-u-15-24"
      }, null, 512), [
        [_vModelText, pageTitle.value]
      ]),
      _withDirectives(_createElementVNode("select", {
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedExample).value = $event)),
        class: "pure-u-8-24"
      }, [
        _hoisted_2,
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(examples), (e) => {
          return (_openBlock(), _createElementBlock("option", { value: e }, _toDisplayString(e.name), 9, _hoisted_3))
        }), 256))
      ], 512), [
        [_vModelSelect, selectedExample.value]
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("img", {
          src: _imports_0,
          alt: "Share on Twitter",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (shareOnTwitter())),
          style: {"width":"2em","height":"2em"}
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(_unref(NippMonacoEditor), {
        modelValue: script.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((script).value = $event)),
        options: _unref(monacoOptions),
        style: {"width":"100%","height":"98%","border":"#ccc solid 2px","box-sizing":"border-box"}
      }, null, 8, ["modelValue", "options"])
    ]),
    _createElementVNode("form", _hoisted_6, [
      _withDirectives(_createElementVNode("select", {
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((transpiler).value = $event)),
        onChange: _cache[5] || (_cache[5] = ($event: any) => (onChangeTranspiler())),
        style: {"margin-right":"1rem"}
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(transpilers.value, (t) => {
          return (_openBlock(), _createElementBlock("option", { value: t }, _toDisplayString(t.name), 9, _hoisted_7))
        }), 256))
      ], 544), [
        [_vModelSelect, transpiler.value]
      ]),
      _createElementVNode("span", _hoisted_8, [
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((enableClickRun).value = $event))
        }, null, 512), [
          [_vModelCheckbox, enableClickRun.value]
        ]),
        _createTextVNode(": click_run")
      ]),
      _createElementVNode("span", _hoisted_9, [
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((consoleClearBeforeRun).value = $event))
        }, null, 512), [
          [_vModelCheckbox, consoleClearBeforeRun.value]
        ]),
        _createTextVNode(": console_clear")
      ]),
      _createElementVNode("details", _hoisted_10, [
        _hoisted_11,
        _hoisted_12,
        _withDirectives(_createElementVNode("select", {
          id: "compression_alg",
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((compressionAlg).value = $event))
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(compressionAlgs.value, (a) => {
            return (_openBlock(), _createElementBlock("option", { value: a }, _toDisplayString(a.name), 9, _hoisted_13))
          }), 256))
        ], 512), [
          [_vModelSelect, compressionAlg.value]
        ]),
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((enablePromiseWait).value = $event))
        }, null, 512), [
          [_vModelCheckbox, enablePromiseWait.value]
        ]),
        _createTextVNode(": promise_wait "),
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((enableTopLevelAwait).value = $event))
        }, null, 512), [
          [_vModelCheckbox, enableTopLevelAwait.value]
        ]),
        _createTextVNode(": top-level await ")
      ]),
      (enableClickRun.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            onClick: _cache[11] || (_cache[11] = ($event: any) => (onClickClickRun())),
            class: "pure-button",
            style: {"color":"white","background":"rgb(28, 184, 65)"}
          }, _toDisplayString(clickRunButtonText.value), 1))
        : _createCommentVNode("", true),
      (false && !enableClickRun.value && blockingExecutionDetected.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 1,
            onClick: _cache[12] || (_cache[12] = ($event: any) => (enableClickRun.value = true)),
            class: "pure-button",
            style: {"color":"white","background":"rgb(255, 165, 0)"}
          }, _toDisplayString("(BLOCKING detected) Enable click_run"), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("form", _hoisted_14, [
      _withDirectives(_createElementVNode("textarea", {
        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((inputText).value = $event)),
        placeholder: "Input",
        rows: "10",
        class: "pure-u-1 pure-u-md-1-2"
      }, null, 512), [
        [_vModelText, inputText.value]
      ]),
      _withDirectives(_createElementVNode("textarea", {
        "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((outputText).value = $event)),
        placeholder: "Output",
        rows: "10",
        class: "pure-u-1 pure-u-md-1-2"
      }, null, 512), [
        [_vModelText, outputText.value]
      ])
    ]),
    _createElementVNode("button", {
      onClick: _cache[15] || (_cache[15] = ($event: any) => (showError.value = !showError.value)),
      style: _normalizeStyle({color: hasError.value? 'red': ''}),
      class: "pure-button"
    }, "Show/Hide error", 4),
    (showError.value)
      ? (_openBlock(), _createElementBlock("span", _hoisted_15, [
          _createElementVNode("div", _hoisted_16, [
            _withDirectives(_createElementVNode("textarea", {
              "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((errorStr).value = $event)),
              class: "pure-u-1"
            }, null, 512), [
              [_vModelText, errorStr.value]
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("button", {
      onClick: _cache[17] || (_cache[17] = ($event: any) => (showTranspiledJsCode.value = !showTranspiledJsCode.value)),
      class: "pure-button"
    }, "Show/Hide transpiled JS code"),
    (showTranspiledJsCode.value)
      ? (_openBlock(), _createElementBlock("span", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("div", _hoisted_19, [
              _createVNode(_unref(NippMonacoEditor), {
                modelValue: transpiledJsCode.value,
                options: { language: 'javascript', fontSize: 12, minimap: { enabled: false } },
                style: {"min-height":"5rem"}
              }, null, 8, ["modelValue"])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})